import { createContext, useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useToken, useUserRbac } from "@/shared/hooks";
import { Flex, Loading } from "@abel-technologies-as/components";
import { UserDetails, UserRbacResponse } from "../types";
import { useTranslation } from "react-i18next";
import { languages } from "@/shared/constants";

interface Props {
	children: string | JSX.Element | JSX.Element[];
}

export interface AuthContextProps {
	user?: UserDetails;
	rbac?: UserRbacResponse;
}

const AuthContext = createContext<AuthContextProps | null>(null);

const AuthProvider = ({ children }: Props) => {
	const searchParams = new URLSearchParams(window.location.search);
	const result = searchParams.get("result");
	const accessToken = searchParams.get("access_token");
	const { i18n } = useTranslation();
	const refreshToken = searchParams.get("refresh_token");
	const message = searchParams.get("message");
	const checkToken = accessToken || localStorage.getItem("accessToken") ? true : false;

	useEffect(() => {
		const url = new URL(window.location.href);
		const params = url.searchParams;
		params.delete("access_token");
		params.delete("refresh_token");
		const pathName = result && result === "error" ? url.pathname + "?" + params.toString() : window.location.pathname;
		window.history.replaceState({}, document.title, pathName);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (result === "success") {
		localStorage.setItem("accessToken", accessToken ?? "");
		localStorage.setItem("refreshToken", refreshToken ?? "");
	}

	const { data, isLoading } = useToken(checkToken);

	const memoizedUser = useMemo(() => {
		const { FirstName, LastName, LanguageId, ProfilePhotoUrl, UserId, Gender, PhoneNumber, CountryCode, Email } =
			data?.data?.[0] || {};

		return {
			id: UserId ? +UserId : undefined,
			firstName: FirstName ?? "",
			lastName: LastName ?? "",
			phoneNumber: PhoneNumber,
			email: Email,
			countryCode: CountryCode,
			languageId: LanguageId ? +LanguageId : undefined,
			gender: Gender,
			profilePicture: ProfilePhotoUrl,
		} as UserDetails;
	}, [data]);

	useEffect(() => {
		if (memoizedUser.languageId) {
			const language = languages.find((lang) => lang.id === memoizedUser.languageId)?.value;
			if (language) {
				i18n.changeLanguage(language);
			}
		}
	}, [memoizedUser, i18n]);

	const {
		data: rbac,
		isLoading: isRbacLoading,
		isError: isRbacError,
	} = useUserRbac(memoizedUser.id, checkToken && !isLoading);

	const isWaitingForRbac = !!memoizedUser.id && !rbac && !isRbacError;

	if (isLoading || isRbacLoading || isWaitingForRbac) {
		return (
			<Flex width="100vw" height="100vh">
				<Loading variant="indeterminate" size="50px" />
			</Flex>
		);
	}

	if (result === "error") {
		return <Navigate to={`/guest?result=${result}&message=${message}`} />;
	}

	if (!accessToken && !localStorage.getItem("accessToken")) {
		return <Navigate to={"/guest"} />;
	}

	return <AuthContext.Provider value={{ user: memoizedUser, rbac }}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
